import React, { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Typography } from '@material-ui/core';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import CanvasRenderer from '../canvas/renderer/CanvasRenderer';
import PageShareControls from '../canvas/components/PageShareControls';
import pagesClient from '../canvas/api/pagesClient';
import localPagesStore from '../canvas/api/localPagesStore';
import {
  kpiCanvas,
  kpiManifest,
  kpiDatasets,
} from '../canvas/fixtures/kpiDashboard';
import { intakeCanvas, intakeManifest } from '../canvas/fixtures/intakeForm';
import config from '../config/environment';

const SCAFFOLD_MAP_URL = `${config.BACKEND_URL}/docdb/scaffolddjangomap`;
const RETRIEVE_PAGE_URL = `${config.EDITOR_URL}/retrievePage`;

/**
 * Resolve human-readable page ids from React Router.
 * Preferred: /page/:tenantId/:pageSlug → tenantId/pageSlug
 * Legacy: /page/:pageId (scaffold / django form id — single segment)
 */
export function resolvePageIdFromParams(params) {
  if (!params) return null;
  if (params.tenantId && params.pageSlug) {
    return `${params.tenantId}/${params.pageSlug}`;
  }
  if (params.pageId && String(params.pageId).includes('/')) {
    return params.pageId;
  }
  return params.pageId || null;
}

export function isLegacyPageId(pageId) {
  return !!pageId && !String(pageId).includes('/');
}

const FIXTURE_PAGES = {
  'tenantabc/cm-office-kpi': {
    canvas: kpiCanvas,
    manifest: kpiManifest,
    datasets: kpiDatasets,
  },
  'tenantabc/customer-intake': {
    canvas: intakeCanvas,
    manifest: intakeManifest,
    datasets: {},
  },
};

async function loadLegacyHtmlPage(scaffoldId) {
  const mapRes = await axios.get(SCAFFOLD_MAP_URL, {
    params: { scaffoldId },
  });
  const rows = mapRes.data || [];
  if (!rows.length) {
    const err = new Error('Legacy page mapping not found');
    err.status = 404;
    throw err;
  }
  const djangoId = rows[0].djangoId;
  const pageRes = await axios.get(RETRIEVE_PAGE_URL, {
    params: { id: djangoId },
  });
  const data = pageRes.data || {};
  const html =
    data.html ||
    data.htmlString ||
    data.html_string ||
    data.content ||
    (typeof data.fields === 'string' ? data.fields : '') ||
    '';
  return {
    kind: 'legacy_html',
    html,
    fields: data.fields,
    djangoId,
    scaffoldId,
  };
}

/**
 * Workload Pages viewer.
 * Resolution order for tenant/slug pages:
 *   1) Pages API (persisted)
 *   2) localStorage bridge (nonproduction / opt-in)
 *   3) built-in fixtures (dev demos)
 *
 * Single-segment /page/:pageId keeps the legacy scaffold → retrievePage HTML path
 * so existing customer links continue to work.
 */
const Page = () => {
  const params = useParams();
  const location = useLocation();
  const pageId = useMemo(() => resolvePageIdFromParams(params), [params]);
  const shareToken = useMemo(() => {
    try {
      return new URLSearchParams(location.search || '').get('share') || '';
    } catch {
      return '';
    }
  }, [location.search]);
  // Draft preview (?preview=1) must be forwarded to the Pages API — without it,
  // unpublished pages 404 even though the draft exists.
  const previewMode = useMemo(() => {
    try {
      const v = new URLSearchParams(location.search || '').get('preview');
      return v === '1' || String(v || '').toLowerCase() === 'true';
    } catch {
      return false;
    }
  }, [location.search]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [doc, setDoc] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      setDoc(null);

      if (!pageId) {
        setError('Missing page id. Use /page/:tenantId/:pageSlug or legacy /page/:pageId');
        setLoading(false);
        return;
      }

      // Legacy single-segment Workload Pages (scaffold / HTML)
      if (isLegacyPageId(pageId)) {
        try {
          const legacy = await loadLegacyHtmlPage(pageId);
          if (!cancelled) {
            setDoc(legacy);
            setLoading(false);
          }
        } catch (e) {
          if (!cancelled) {
            setError(e.message || 'Failed to load legacy page');
            setLoading(false);
          }
        }
        return;
      }

      // New canvas pages: API → local store → fixtures
      try {
        const remote = await pagesClient.getPage(pageId, {
          preview: previewMode,
          shareToken: shareToken || undefined,
        });
        if (!cancelled) {
          setDoc({
            kind: 'canvas',
            canvas: remote.canvas,
            manifest: remote.manifest,
            datasets: remote.datasets,
            page: remote.page || null,
            source: 'api',
            preview: previewMode || remote.kind === 'draft_preview',
          });
          setLoading(false);
        }
        return;
      } catch (apiErr) {
        // fall through
        if (apiErr && apiErr.status === 403) {
          if (!cancelled) {
            setError('You do not have access to this page.');
            setLoading(false);
          }
          return;
        }
      }

      if (localPagesStore.enabled()) {
        const local = localPagesStore.getPublished(pageId);
        if (local && !cancelled) {
          setDoc({
            kind: 'canvas',
            canvas: local.canvas,
            manifest: local.manifest,
            datasets: local.datasets,
          });
          setLoading(false);
          return;
        }
      }

      const fixture = FIXTURE_PAGES[pageId];
      if (fixture && !cancelled) {
        setDoc({ kind: 'canvas', ...fixture });
        setLoading(false);
        return;
      }

      if (!cancelled) {
        setError(
          previewMode
            ? `Draft preview not found: ${pageId}`
            : `Page not found: ${pageId}`,
        );
        setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [pageId, shareToken, previewMode]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography variant="h6">Page unavailable</Typography>
        <Typography color="textSecondary">{error}</Typography>
      </Box>
    );
  }

  if (doc && doc.kind === 'legacy_html') {
    if (!doc.html) {
      return (
        <Box p={3}>
          <Typography variant="h6">Legacy page</Typography>
          <Typography color="textSecondary">
            Page loaded but no HTML content was returned from retrievePage.
          </Typography>
        </Box>
      );
    }
    return (
      <div
        className="workload-legacy-page"
        // Legacy pages historically rendered server-authored HTML.
        dangerouslySetInnerHTML={{ __html: doc.html }}
      />
    );
  }

  const pageMeta = doc.page || {};
  const visibilityPolicy =
    (pageMeta.visibility && pageMeta.visibility.policy) ||
    (doc.canvas && doc.canvas.visibility && doc.canvas.visibility.policy) ||
    'private';
  const pageTenantId =
    pageMeta.tenant_id ||
    (doc.canvas && doc.canvas.tenant_id) ||
    (pageId && String(pageId).split('/')[0]) ||
    '';

  return (
    <Box style={{ background: '#F5F7FB', minHeight: '100vh', position: 'relative' }}>
      {doc.preview ? (
        <Box
          px={2}
          py={1}
          style={{
            background: 'rgba(25, 118, 210, 0.08)',
            borderBottom: '1px solid rgba(25, 118, 210, 0.2)',
          }}
        >
          <Typography variant="body2" color="textSecondary">
            Draft preview — not published yet. Public visitors will not see this page until
            Publish is approved.
          </Typography>
        </Box>
      ) : null}
      {/* Owner share UI for API-backed canvases (hidden for bare share-link viewers). */}
      {doc.kind === 'canvas' && doc.source === 'api' && !shareToken ? (
        <PageShareControls
          pageId={pageId}
          pageTenantId={pageTenantId}
          initialPolicy={visibilityPolicy}
        />
      ) : null}
      <CanvasRenderer
        canvas={doc.canvas}
        manifest={doc.manifest}
        datasets={doc.datasets}
      />
    </Box>
  );
};

export default Page;
